import axios from 'axios';
import OrganizationLookupStep from './components/OrganizationLookupStep';
import OrganizationRegistrationStepper from './components/OrganizationRegistrationStepper';
import OrganizationReviewStep from './components/OrganizationReviewStep';
import { AddressSuggestionItem, getAddressEnSuggestion } from '@/shared/api/suggestions';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  emptyOrganizationRegistrationForm,
  OrganizationInnDraft,
  OrganizationRegistrationFormState,
  OrganizationRegistrationModifiedFields,
  OrganizationRegistrationStage,
  OrganizationRegistrationTouched,
  OrgRegistrationLocationState,
  suggestionToOrganizationRegistrationForm
} from './types';
import { FormEvent, useRef, useState } from 'react';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import {
  getInnInputError,
  getOrgRegistrationBackendFieldErrorText,
  getOrganizationRegistrationValidation,
  toRegisterOrganizationPayload
} from './model';
import { getOrganizationSuggestionApi, registerOrganizationApi } from '../../api';
import { organizationQueryKey } from '@/features/Organization';
import { toast } from '@/components/ui/sonner';
import { useAuthStore } from '../../model/authStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const OrgRegistrationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locationState = location.state as OrgRegistrationLocationState | null;
  const draft = locationState?.draft;
  const returnTo = locationState?.returnTo ?? '/profile';

  const setAuthOrganizationId = useAuthStore((state) => state.setOrganizationId);

  const [draftsByInn, setDraftsByInn] = useState<Record<string, OrganizationInnDraft>>(() => {
    const trimmedInn = draft?.form?.inn?.trim();

    if (trimmedInn) {
      return {
        [trimmedInn]: {
          form: draft.form,
          modifiedFields: draft.modifiedFields ?? {},
          touched: draft.touched
        }
      };
    }

    return {};
  });
  const [form, setForm] = useState<OrganizationRegistrationFormState>(
    draft?.form ?? emptyOrganizationRegistrationForm
  );
  const [innInput, setInnInput] = useState(draft?.innInput ?? '');
  const [innTouched, setInnTouched] = useState(draft?.isInnTouched ?? false);
  const [isOfferAccepted, setIsOfferAccepted] = useState(draft?.isOfferAccepted ?? false);
  const [isRefreshingOrganizationAccess, setIsRefreshingOrganizationAccess] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [modifiedFields, setModifiedFields] = useState<OrganizationRegistrationModifiedFields>(
    draft?.modifiedFields ?? {}
  );
  const [registeredInnError, setRegisteredInnError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<{
    address?: string;
    addressEn?: string;
    fullNameEn?: string;
  }>({});
  const [stage, setStage] = useState<OrganizationRegistrationStage>(draft?.stage ?? 'lookup');
  const [submitAttempted, setSubmitAttempted] = useState(draft?.submitAttempted ?? false);

  const initialTouched: OrganizationRegistrationTouched = {
    address: false,
    addressEn: false,
    fullName: false,
    fullNameEn: false,
    kpp: false,
    ogrn: false,
    phone: false,
    shortName: false
  };
  const [touched, setTouched] = useState<OrganizationRegistrationTouched>(
    draft?.touched ?? initialTouched
  );

  const latestLookupInnRef = useRef<string | null>(null);

  const onBlurField = (key: keyof OrganizationRegistrationTouched): void => {
    setTouched((prev) => {
      const nextTouched = { ...prev, [key]: true };
      const trimmedInn = form.inn.trim();

      if (trimmedInn) {
        setDraftsByInn((prevDrafts) => {
          const currentDraft = prevDrafts[trimmedInn];
          if (!currentDraft) return prevDrafts;

          return {
            ...prevDrafts,
            [trimmedInn]: {
              ...currentDraft,
              touched: nextTouched
            }
          };
        });
      }

      return nextTouched;
    });
  };

  const innInputError = getInnInputError(innInput);
  const rawValidation = getOrganizationRegistrationValidation(form);
  const isAddressServiceUnavailable = serverFieldErrors.address === 'Проверка адресов временно недоступна, попробуйте позже';
  const validation = {
    ...rawValidation,
    addressEnError: rawValidation.addressEnError || serverFieldErrors.addressEn || '',
    addressError: rawValidation.addressError || serverFieldErrors.address || '',
    areFormFieldsValid: rawValidation.areFormFieldsValid
      && (!serverFieldErrors.address || isAddressServiceUnavailable)
      && !serverFieldErrors.addressEn
      && !serverFieldErrors.fullNameEn,
    fullNameEnError: rawValidation.fullNameEnError || serverFieldErrors.fullNameEn || ''
  };
  const canSubmitForm = validation.areFormFieldsValid && isOfferAccepted;

  const scrollToFirstError = (overrideServerErrors = serverFieldErrors): void => {
    const fieldOrder: string[] = [
      ...(validation.fullNameError ? ['fullName'] : []),
      ...((rawValidation.fullNameEnError || overrideServerErrors.fullNameEn) ? ['fullNameEn'] : []),
      ...(validation.shortNameError ? ['shortName'] : []),
      ...(!validation.isIndividualEntrepreneur && validation.kppError ? ['kpp'] : []),
      ...(validation.ogrnError ? ['ogrn'] : []),
      ...((rawValidation.addressError || overrideServerErrors.address) ? ['address'] : []),
      ...((rawValidation.addressEnError || overrideServerErrors.addressEn) ? ['addressEn'] : []),
      ...(validation.phoneError ? ['phone'] : []),
      ...(!isOfferAccepted ? ['offer-section'] : [])
    ];

    const firstErrorId = fieldOrder[0];
    if (!firstErrorId) return;

    const element = document.getElementById(firstErrorId);
    if (element && 'scrollIntoView' in element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    element?.focus();
  };

  const {
    mutate: mutateGetAddressEnSuggestion
  } = useMutation({
    mutationFn: getAddressEnSuggestion,
    mutationKey: ['getAddressEnSuggestion', location.pathname],
    onError: (error) => {
      toast.error('Ошибка', {
        description: getErrorMessage(error, 'Не удалось перевести адрес')
      });
    },
    onSuccess: (data, variables) => {
      const addressEn = data?.addressEn ?? '';
      setForm((prev) => {
        if (!prev.isAddressSelected || prev.address !== variables.value) {
          return prev;
        }

        const nextForm = { ...prev, addressEn };
        const trimmedInn = nextForm.inn.trim();

        if (trimmedInn) {
          setDraftsByInn((prevDrafts) => {
            const currentDraft = prevDrafts[trimmedInn];
            if (!currentDraft) return prevDrafts;

            return {
              ...prevDrafts,
              [trimmedInn]: {
                ...currentDraft,
                form: nextForm
              }
            };
          });
        }

        return nextForm;
      });
    }
  });

  const onSelectAddressSuggestion = (suggestion: AddressSuggestionItem): void => {
    const isComplete = suggestion.complete !== false;
    const unrestrictedValue = suggestion.unrestrictedValue
      ?? suggestion.unrestricted_value
      ?? suggestion.value;
    const fiasId = suggestion.fiasId ?? suggestion.fias_id ?? '';

    setForm((prev) => {
      const nextForm: OrganizationRegistrationFormState = {
        ...prev,
        address: unrestrictedValue,
        addressCompleteError: isComplete ? '' : (suggestion.message || 'Уточните адрес до дома или участка'),
        addressEn: '',
        addressFiasId: fiasId,
        isAddressSelected: true
      };

      const trimmedInn = nextForm.inn.trim();
      if (trimmedInn) {
        setDraftsByInn((prevDrafts) => {
          const currentDraft = prevDrafts[trimmedInn];
          return {
            ...prevDrafts,
            [trimmedInn]: {
              form: nextForm,
              modifiedFields: {
                ...(currentDraft?.modifiedFields ?? modifiedFields),
                address: true
              },
              touched: {
                ...(currentDraft?.touched ?? touched),
                address: true
              }
            }
          };
        });
      }

      return nextForm;
    });

    setModifiedFields((prev) => ({ ...prev, address: true }));
    setTouched((prev) => ({ ...prev, address: true }));
    setServerFieldErrors((prev) => ({ ...prev, address: undefined }));

    if (isComplete) {
      mutateGetAddressEnSuggestion({ value: unrestrictedValue });
    }
  };

  const onAddressTextChange = (text: string): void => {
    setForm((prev) => {
      const nextForm: OrganizationRegistrationFormState = {
        ...prev,
        address: text,
        addressCompleteError: '',
        addressEn: '',
        addressFiasId: '',
        isAddressSelected: false
      };

      const trimmedInn = nextForm.inn.trim();
      if (trimmedInn) {
        setDraftsByInn((prevDrafts) => {
          const currentDraft = prevDrafts[trimmedInn];
          return {
            ...prevDrafts,
            [trimmedInn]: {
              form: nextForm,
              modifiedFields: {
                ...(currentDraft?.modifiedFields ?? modifiedFields),
                address: true
              },
              touched: {
                ...(currentDraft?.touched ?? touched),
                address: true
              }
            }
          };
        });
      }

      return nextForm;
    });

    setModifiedFields((prev) => ({ ...prev, address: true }));
    setTouched((prev) => ({ ...prev, address: true }));
    setServerFieldErrors((prev) => ({ ...prev, address: undefined }));
  };

  const onClearAddressSelection = (): void => {
    setForm((prev) => {
      if (!prev.isAddressSelected && !prev.addressFiasId && !prev.addressEn && !prev.addressCompleteError) {
        return prev;
      }
      return {
        ...prev,
        addressCompleteError: '',
        addressEn: '',
        addressFiasId: '',
        isAddressSelected: false
      };
    });
  };

  const {
    isPending: isPendingGetOrganizationSuggestionApi,
    mutate: mutateGetOrganizationSuggestionApi
  } = useMutation({
    mutationFn: getOrganizationSuggestionApi,
    mutationKey: ['getOrganizationSuggestionApi', location.pathname],
    onError: (error) => {
      toast.error('Ошибка', {
        description: getErrorMessage(error, 'Не удалось найти организацию')
      });
    },
    onSuccess: (data, variables) => {
      const trimmedInn = innInput.trim();
      if (variables !== trimmedInn || variables !== latestLookupInnRef.current) return;

      if (data.isRegistered === true) {
        setRegisteredInnError('Организация с таким ИНН уже зарегистрирована');
        return;
      }

      setRegisteredInnError(null);

      const existingDraft = draftsByInn[variables];
      const nextForm = suggestionToOrganizationRegistrationForm(data, existingDraft);

      setForm(nextForm);
      setIsOfferAccepted(false);
      setModifiedFields(existingDraft?.modifiedFields ?? {});
      setServerFieldErrors({});
      setStage('review');
      setSubmitAttempted(false);
      setTouched(existingDraft ? existingDraft.touched : initialTouched);
      toast.success('Данные найдены', {
        description: 'Проверьте реквизиты и при необходимости отредактируйте.'
      });
    }
  });

  const activateOrganizationAccess = async (orgId: string): Promise<void> => {
    setIsRefreshingOrganizationAccess(true);
    try {
      await setAuthOrganizationId(orgId);
      toast.success('Организация зарегистрирована', {
        description: 'Реквизиты сохранены успешно.'
      });
      queryClient.invalidateQueries({ queryKey: organizationQueryKey(orgId) });
      navigate(returnTo, { replace: true });
    } catch (error) {
      toast.error('Организация зарегистрирована', {
        description: getErrorMessage(error, 'Доступ ещё не обновлён. Нажмите «Обновить доступ» или войдите снова.')
      });
    } finally {
      setIsRefreshingOrganizationAccess(false);
    }
  };

  const {
    isPending: isPendingRegisterOrganizationApi,
    mutate: mutateRegisterOrganizationApi
  } = useMutation({
    mutationFn: registerOrganizationApi,
    mutationKey: ['registerOrganizationApi', location.pathname],
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          const nextServerErrors = {
            ...serverFieldErrors,
            address: 'Проверка адресов временно недоступна, попробуйте позже'
          };
          setServerFieldErrors(nextServerErrors);
          toast.error('Ошибка', {
            description: 'Проверка адресов временно недоступна, попробуйте позже'
          });
          return;
        }

        if (error.response?.status === 400) {
          const fieldErrors = error.response?.data?.details?.field_errors;

          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            const nextServerErrors: { address?: string; addressEn?: string; fullNameEn?: string } = {};

            for (const item of fieldErrors) {
              if (!item?.field) continue;

              const fieldName = item.field;

              if (fieldName === 'fullNameEn' || fieldName === 'full_name_en') {
                nextServerErrors.fullNameEn = getOrgRegistrationBackendFieldErrorText('fullNameEn', item);
              } else if (fieldName === 'addressEn' || fieldName === 'address_en') {
                nextServerErrors.addressEn = getOrgRegistrationBackendFieldErrorText('addressEn', item);
              } else if (fieldName === 'address') {
                nextServerErrors.address = getOrgRegistrationBackendFieldErrorText('address', item);
              }
            }

            if (Object.keys(nextServerErrors).length > 0) {
              setServerFieldErrors(nextServerErrors);
              setTimeout(() => {
                scrollToFirstError(nextServerErrors);
              }, 0);
            }
          }
        }
      }

      toast.error('Ошибка', {
        description: getErrorMessage(error, 'Ошибка регистрации организации')
      });
    },
    onSuccess: async (data) => {
      setPendingOrgId(data.org_id);
      await activateOrganizationAccess(data.org_id);
    }
  });

  const onBackToLookup = (): void => {
    const trimmedInn = form.inn.trim();

    if (trimmedInn) {
      setDraftsByInn((prevDrafts) => ({
        ...prevDrafts,
        [trimmedInn]: {
          form,
          modifiedFields,
          touched
        }
      }));
    }

    setIsOfferAccepted(false);
    setRegisteredInnError(null);
    setServerFieldErrors({});
    setStage('lookup');
    setSubmitAttempted(false);
    setTouched(initialTouched);
  };

  const onChangeField = <K extends keyof OrganizationRegistrationFormState>(
    key: K,
    value: OrganizationRegistrationFormState[K]
  ): void => {
    setForm((currentForm) => {
      const nextForm = { ...currentForm, [key]: value };
      const trimmedInn = nextForm.inn.trim();

      if (trimmedInn) {
        setDraftsByInn((prevDrafts) => {
          const currentDraft = prevDrafts[trimmedInn];

          return {
            ...prevDrafts,
            [trimmedInn]: {
              form: nextForm,
              modifiedFields: {
                ...(currentDraft?.modifiedFields ?? modifiedFields),
                [key]: true
              },
              touched: {
                ...(currentDraft?.touched ?? touched),
                [key]: true
              }
            }
          };
        });
      }

      return nextForm;
    });

    setModifiedFields((prev) => ({ ...prev, [key]: true }));
    setTouched((prev) => ({ ...prev, [key]: true }));

    if (key === 'fullNameEn' || key === 'addressEn' || key === 'address') {
      setServerFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const onChangeInnInput = (value: string): void => {
    setInnInput(value);
    if (registeredInnError) {
      setRegisteredInnError(null);
    }
  };

  const onLookupOrganization = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setInnTouched(true);
    if (innInputError) return;

    const trimmedInn = innInput.trim();
    latestLookupInnRef.current = trimmedInn;
    mutateGetOrganizationSuggestionApi(trimmedInn);
  };

  const onSubmitOrganization = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (isPendingRegisterOrganizationApi || isRefreshingOrganizationAccess) return;
    if (pendingOrgId) {
      void activateOrganizationAccess(pendingOrgId);
      return;
    }
    setSubmitAttempted(true);

    if (isAddressServiceUnavailable) {
      setServerFieldErrors((prev) => ({ ...prev, address: undefined }));
    }

    if (!canSubmitForm) {
      scrollToFirstError();
      return;
    }

    mutateRegisterOrganizationApi(toRegisterOrganizationPayload(form));
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        animate={{ opacity: 1, scale: 1, y: 0 }}
        initial={{ opacity: 0, scale: 0.98, y: 16 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
      >
        <Card className="relative">
          <button
            className="absolute left-4 top-4 z-10 flex items-center justify-center size-11 rounded-full p-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Закрыть"
            onClick={() => navigate(returnTo)}
            type="button"
          >
            <X className="size-5" />
          </button>
          <CardHeader className="space-y-3">
            <motion.div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground"
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              initial={{ opacity: 0, rotate: -12, scale: 0.85 }}
              transition={{ delay: 0.1, duration: 0.25 }}
            >
              <Building2 className="h-6 w-6" />
            </motion.div>
            <CardTitle className="text-center">Регистрация организации</CardTitle>
            <CardDescription className="text-center">
              Введите ИНН - мы автоматически подгрузим реквизиты
            </CardDescription>
            <OrganizationRegistrationStepper stage={stage} />
          </CardHeader>

          <CardContent>
            <AnimatePresence
              initial={false}
              mode="wait"
            >
              {stage === 'lookup' ? (
                <OrganizationLookupStep
                  innInput={innInput}
                  innInputError={innInputError}
                  isInnTouched={innTouched}
                  isPending={isPendingGetOrganizationSuggestionApi}
                  key="lookup-step"
                  onBlurInnInput={() => setInnTouched(true)}
                  onChangeInnInput={onChangeInnInput}
                  onSubmit={onLookupOrganization}
                  registeredInnError={registeredInnError ?? undefined}
                />
              ) : (
                <OrganizationReviewStep
                  form={form}
                  hasRegisteredOrganization={Boolean(pendingOrgId)}
                  isIndividualEntrepreneur={validation.isIndividualEntrepreneur}
                  isOfferAccepted={isOfferAccepted}
                  isPending={isPendingRegisterOrganizationApi || isRefreshingOrganizationAccess}
                  key="review-step"
                  onAcceptedChange={setIsOfferAccepted}
                  onAddressTextChange={onAddressTextChange}
                  onBack={onBackToLookup}
                  onBlurField={onBlurField}
                  onChangeField={onChangeField}
                  onClearAddressSelection={onClearAddressSelection}
                  onSelectAddressSuggestion={onSelectAddressSuggestion}
                  onSubmit={onSubmitOrganization}
                  submitAttempted={submitAttempted}
                  touched={touched}
                  validation={validation}
                />
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default OrgRegistrationForm;